import * as React from 'react';
import { Alert, Box, Button, CircularProgress, TextField } from '@mui/material';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import SearchIcon from '@mui/icons-material/Search';
import { formatBusinessId } from '../../utils/ytj';

/**
 * The Y-tunnus field with its "Fetch from YTJ" button and result banner, and the
 * e-invoice address lookup button. Driven by useCompanyLookup(); used by both the
 * full customer dialog and the inline NewCustomerForm, so every place a customer
 * can be created offers the same lookups.
 */

// Line the button up with the text field next to it.
const BUTTON_HEIGHT = { small: 40, medium: 56 };

export function BusinessIdLookupField({ t, lookup, value, onChange, size = 'medium', id, error, helperText, sx }) {
  const { businessIdValid, ytjLoading, ytjResult, fetchFromYtj, onBusinessIdTyped, dismissYtjResult } = lookup;

  const handleChange = (e) => {
    const businessId = formatBusinessId(e.target.value);
    onChange(businessId);
    onBusinessIdTyped(businessId);
  };

  // An empty field says what it is for, so the YTJ lookup is discoverable before
  // anyone has typed a Y-tunnus.
  let hint = '';
  if (!value) hint = t('Enter the Business ID to fetch the company details from YTJ');
  else if (!businessIdValid) hint = t('Check the Business ID (format 1234567-8)');

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1.5,
          alignItems: { xs: 'stretch', sm: 'flex-start' },
        }}
      >
        <TextField
          id={id}
          name="business_id"
          label={t('Business ID')}
          onChange={handleChange}
          fullWidth
          size={size}
          value={value || ''}
          placeholder="1234567-8"
          error={!!error}
          helperText={helperText || hint}
          inputProps={{ maxLength: 9, inputMode: 'numeric' }}
          sx={sx}
        />
        {/* Always in sight, so the lookup is easy to find and to run again for a
            different Y-tunnus; it wakes up once the Y-tunnus is valid. */}
        <Button
          variant="outlined"
          onClick={fetchFromYtj}
          disabled={ytjLoading || !businessIdValid}
          data-testid="fetch-from-ytj-button"
          startIcon={ytjLoading ? <CircularProgress size={16} color="inherit" /> : <CloudDownloadOutlinedIcon />}
          sx={{
            borderRadius: '10px',
            whiteSpace: 'nowrap',
            flexShrink: 0,
            height: BUTTON_HEIGHT[size] || BUTTON_HEIGHT.medium,
            px: 2,
          }}
        >
          {ytjLoading ? t('Fetching...') : t('Fetch from YTJ')}
        </Button>
      </Box>

      {ytjResult && (
        <Alert
          severity={ytjResult.severity}
          data-testid="ytj-lookup-result"
          onClose={dismissYtjResult}
          sx={{ borderRadius: '10px' }}
        >
          {ytjResult.message}
        </Alert>
      )}
    </>
  );
}

export function EInvoiceLookupButton({ t, lookup, sx }) {
  const {
    canLookupVerkkolasku,
    verkkolaskuLoading,
    verkkolaskuResult,
    lookupVerkkolasku,
    dismissVerkkolaskuResult,
  } = lookup;

  return (
    <>
      {canLookupVerkkolasku && (
        <Button
          variant="outlined"
          onClick={lookupVerkkolasku}
          disabled={verkkolaskuLoading}
          data-testid="fetch-verkkolasku-button"
          fullWidth
          startIcon={verkkolaskuLoading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
          sx={{ borderRadius: '10px', justifyContent: 'center', py: 1.25, ...sx }}
        >
          {verkkolaskuLoading ? t('Searching...') : t('Search e-invoice address by Business ID')}
        </Button>
      )}

      {verkkolaskuResult && (
        <Alert
          severity={verkkolaskuResult.severity}
          data-testid="verkkolasku-lookup-result"
          sx={{ borderRadius: '10px', ...sx }}
          onClose={dismissVerkkolaskuResult}
        >
          {verkkolaskuResult.message}
        </Alert>
      )}
    </>
  );
}
